import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Search, ShoppingCart, ChevronDown, X, Plus, Trash2, ExternalLink } from 'lucide-react'
import { ordersService } from '@/services/ordersService'
import { useDebounce } from '@/hooks/useDebounce'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import StatusBadge from '@/components/shared/StatusBadge'
import { formatCurrency, formatDateTime, ORDER_STATUS_LABELS } from '@/utils/formatters'
import { ROUTES } from '@/constants/routes'

const ORDER_STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled']
const PAYMENT_STATUSES = ['pending','completed','failed','refunded']

// ── Inline-editable status cell ───────────────────────────────────────────────
// Click the badge → becomes a select dropdown → change auto-saves, blur cancels.
function InlineStatusCell({ order, onSaved, lang, t }) {
  const [editing, setEditing] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const selectRef = useRef(null)

  useEffect(() => {
    if (editing && selectRef.current) selectRef.current.focus()
  }, [editing])

  const handleChange = async (e) => {
    const newStatus = e.target.value
    if (newStatus === order.status) { setEditing(false); return }
    setSaving(true)
    setEditing(false)
    try {
      await ordersService.updateStatus(order.orderNumber, newStatus)
      toast.success(t('orders.statusUpdated'))
      onSaved()
    } catch {
      toast.error(t('orders.statusUpdateFailed'))
    }
    setSaving(false)
  }

  if (saving) return (
    <div style={{ display:'flex', alignItems:'center', gap:6, padding:'2px 0' }}>
      <LoadingSpinner size={14} />
    </div>
  )

  if (editing) return (
    <div style={{ position:'relative', display:'inline-flex', minWidth:150 }}>
      <select
        ref={selectRef}
        defaultValue={order.status}
        onChange={handleChange}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        style={{
          background: '#E2E2E2',
          border: 'none',
          borderRadius: 10,
          padding: '6px 28px 6px 10px',
          fontSize: 13,
          fontFamily: 'Cairo, sans-serif',
          fontWeight: 700,
          color: '#1A1C1C',
          outline: '2px solid rgba(0,6,102,0.25)',
          cursor: 'pointer',
          appearance: 'none',
          width: '100%',
        }}
      >
        {ORDER_STATUSES.map((s) => (
          <option key={s} value={s}>{ORDER_STATUS_LABELS[s]?.[lang === 'ar' ? 'ar' : 'en'] || s}</option>
        ))}
      </select>
      <ChevronDown size={12} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
    </div>
  )

  return (
    <button
      onClick={() => setEditing(true)}
      title={t('orders.clickToChangeStatus')}
      style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'inline-flex', alignItems:'center', borderRadius:8, outline:'none' }}
    >
      <StatusBadge status={order.status} type="order" lang={lang} />
    </button>
  )
}

export default function OrdersPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()
  const navigate = useNavigate()

  const [search,        setSearch]        = useState('')
  const [statusFilter,  setStatusFilter]  = useState('')
  const [paymentFilter, setPaymentFilter] = useState('')
  const { page, limit, setPage } = usePagination(1, 20)
  const debouncedSearch = useDebounce(search, 400)

  // ── Add Order state ────────────────────────────────────────────────────────
  const EMPTY_ORDER = {
    userId: '', paymentMethod: 'cash_on_delivery',
    street: '', city: '', country: 'SA', recipientName: '', phone: '',
    items: [{ productId: '', quantity: 1 }],
    note: '',
  }
  const [addOrderOpen, setAddOrderOpen] = useState(false)
  const [newOrder,     setNewOrder]     = useState(EMPTY_ORDER)

  const addItem    = () => setNewOrder(d => ({ ...d, items: [...d.items, { productId:'', quantity:1 }] }))
  const removeItem = (i) => setNewOrder(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }))
  const setItem    = (i, key, val) => setNewOrder(d => {
    const items = [...d.items]; items[i] = { ...items[i], [key]: val }; return { ...d, items }
  })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['orders-admin', { search:debouncedSearch, statusFilter, paymentFilter, page, limit }],
    queryFn: () => ordersService.getAll({
      search:        debouncedSearch || undefined,
      status:        statusFilter    || undefined,
      paymentStatus: paymentFilter   || undefined,
      page, limit,
    }),
  })

  const orders = data?.data || []
  const meta   = data?.meta || {}

  const handleOrderSaved = () => {
    qc.invalidateQueries({ queryKey: ['orders-admin'] })
    qc.invalidateQueries({ queryKey: ['orders-recent'] })
  }

  const handleSubmitOrder = () => {
    ordersService.create({
      userId: newOrder.userId,
      paymentMethod: newOrder.paymentMethod,
      shippingAddress: {
        recipientName: newOrder.recipientName,
        phone: newOrder.phone,
        street: newOrder.street,
        city: newOrder.city,
        country: newOrder.country,
      },
      items: newOrder.items.filter(i => i.productId.trim()),
      note: newOrder.note || undefined,
    }).then(() => {
      toast.success(t('orders.createSuccess'))
      handleOrderSaved()
      setAddOrderOpen(false)
      setNewOrder(EMPTY_ORDER)
    }).catch((e) => {
      toast.error(e?.response?.data?.message || t('orders.createError'))
    })
  }

  const handleSearch = (v) => { setSearch(v); setPage(1) }

  const PAYMENT_METHODS = [
    { value:'card',            label:t('orders.paymentCard') },
    { value:'cash_on_delivery',label:t('orders.paymentCash') },
    { value:'wallet',          label:t('orders.paymentWallet') },
  ]
  const PAYMENT_LABEL = { card:t('orders.paymentCard'), cash_on_delivery:t('orders.paymentCash'), cod:t('orders.paymentCash'), online:t('orders.paymentOnline'), bank_transfer:t('orders.paymentBankTransfer'), wallet:t('orders.paymentWallet') }
  const PAYMENT_STATUS_LABEL = { pending:t('orders.paymentStatusPending'), completed:t('orders.paymentStatusCompleted'), failed:t('orders.paymentStatusFailed'), refunded:t('orders.paymentStatusRefunded') }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-52">
          <Search size={16} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
          <input className="hg-input" style={{ paddingRight:40 }} placeholder={t('orders.search')} value={search} onChange={(e) => handleSearch(e.target.value)} />
        </div>
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-36" style={{ paddingLeft:28 }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
            <option value="">{t('orders.allStatus')}</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s]?.[lang === 'ar' ? 'ar' : 'en'] || s}</option>)}
          </select>
          <ChevronDown size={14} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
        </div>
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-36" style={{ paddingLeft:28 }} value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setPage(1) }}>
            <option value="">{t('orders.allPayment')}</option>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{PAYMENT_STATUS_LABEL[s]}</option>)}
          </select>
          <ChevronDown size={14} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
        </div>
        {meta.total != null && (
          <span style={{ padding:'6px 12px', borderRadius:12, fontSize:13, fontWeight:700, background:'rgba(0,6,102,0.06)', color:'#000666', fontFamily:'Cairo, sans-serif' }}>
            {t('orders.orderCount', { count: meta.total?.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en') })}
          </span>
        )}
        <button
          onClick={() => setAddOrderOpen(true)}
          className="flex items-center gap-2 text-sm font-bold text-white flex-shrink-0"
          style={{ background:'#000666', padding:'9px 18px', borderRadius:12, fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', boxShadow:'0 8px 24px rgba(26,35,126,0.18)' }}
        >
          <Plus size={16} />
          {t('orders.addOrder')}
        </button>
      </div>

      {/* Table */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p style={{ color:'#BA1A1A', fontWeight:700, fontFamily:'Cairo, sans-serif' }}>{t('common.loadFailed')}</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">{t('common.retry')}</button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState icon={ShoppingCart} message={t('orders.emptyMessage')} />
        ) : (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F9F9F9', borderBottom:'2px solid #F3F3F3' }}>
                    {[t('orders.orderNumber'), t('orders.customer'), t('orders.itemsHeader'), t('orders.shippingHeader'), t('orders.total'), t('orders.payment'), t('orders.status'), t('orders.date'), ''].map((h,i) => (
                      <th key={i} style={{ textAlign:'right', padding:'12px 16px', fontSize:11, fontWeight:700, color:'#767683', whiteSpace:'nowrap', fontFamily:'Tajawal, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, idx) => (
                    <tr key={order.id} style={{ borderBottom: idx === orders.length-1 ? 'none' : '1px solid #F3F3F3', transition:'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background='#F9F9F9'}
                      onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
                    >
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#000666' }}>{order.orderNumber}</span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:600, fontSize:13, color:'#1A1C1C' }}>{order.user?.name || '—'}</div>
                        <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:11, color:'#767683' }}>{order.user?.email || ''}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#454652' }}>{t('orders.itemsCount', { count: order._count?.items || 0 })}</span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#0077B6' }}>{formatCurrency(order.shippingFee || 0)}</span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#FF6D00' }}>{formatCurrency(order.total)}</span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <StatusBadge status={order.paymentStatus} type="payment" lang={lang} />
                        <div style={{ fontSize:11, color:'#767683', fontFamily:'Tajawal, sans-serif', marginTop:4 }}>{PAYMENT_LABEL[order.paymentMethod] || order.paymentMethod}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <InlineStatusCell order={order} onSaved={handleOrderSaved} lang={lang} t={t} />
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>{formatDateTime(order.createdAt, lang === 'ar' ? 'ar-EG' : 'en')}</span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <button onClick={() => navigate(ROUTES.ORDER_DETAIL.replace(':orderNumber', order.orderNumber))}
                          style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(0,6,102,0.06)', color:'#000666', border:'none', cursor:'pointer', whiteSpace:'nowrap' }}
                          onMouseEnter={(e) => e.currentTarget.style.background='rgba(0,6,102,0.14)'}
                          onMouseLeave={(e) => e.currentTarget.style.background='rgba(0,6,102,0.06)'}
                        >
                          <ExternalLink size={11} />{t('orders.details')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding:'0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {/* ── Add Order dialog ─────────────────────────────────────────────── */}
      {addOrderOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }}
          onClick={() => setAddOrderOpen(false)}>
          <div className="animate-in" style={{ background:'#fff', borderRadius:24, padding:28, width:'100%', maxWidth:540, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 64px rgba(0,0,0,0.2)', position:'relative' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setAddOrderOpen(false)} style={{ position:'absolute', top:16, left:16, width:32, height:32, borderRadius:10, background:'#F3F3F3', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#767683' }}><X size={16}/></button>

            <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:18, color:'#000666', marginBottom:20 }}>{t('orders.addOrderTitle')}</h3>

            {/* User ID */}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('orders.userIdLabel')}</label>
              <input className="hg-input" placeholder="uuid..." dir="ltr" value={newOrder.userId}
                onChange={e => setNewOrder(d => ({ ...d, userId: e.target.value }))}
                style={{ fontFamily:'monospace', fontSize:13 }} />
            </div>

            {/* Payment method */}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('orders.paymentMethodLabel')}</label>
              <div style={{ display:'flex', gap:8 }}>
                {PAYMENT_METHODS.map(({ value, label }) => (
                  <button key={value} onClick={() => setNewOrder(d => ({ ...d, paymentMethod: value }))}
                    style={{ flex:1, padding:'9px 0', borderRadius:10, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', transition:'all 0.15s',
                      background: newOrder.paymentMethod === value ? '#000666' : '#F3F3F3',
                      color: newOrder.paymentMethod === value ? '#fff' : '#454652' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Shipping address */}
            <p style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:13, color:'#454652', marginBottom:10 }}>{t('orders.shippingAddressLabel')}</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.recipientNameLabel')}</label>
                <input className="hg-input" placeholder={t('orders.recipientNamePlaceholder')} value={newOrder.recipientName}
                  onChange={e => setNewOrder(d => ({ ...d, recipientName: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
              </div>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.phoneLabel')}</label>
                <input className="hg-input" placeholder="+966..." dir="ltr" value={newOrder.phone}
                  onChange={e => setNewOrder(d => ({ ...d, phone: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
              </div>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.cityLabel')}</label>
                <input className="hg-input" placeholder={t('orders.cityPlaceholder')} value={newOrder.city}
                  onChange={e => setNewOrder(d => ({ ...d, city: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
              </div>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.countryLabel')}</label>
                <input className="hg-input" placeholder="SA" dir="ltr" value={newOrder.country}
                  onChange={e => setNewOrder(d => ({ ...d, country: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.streetLabel')}</label>
              <input className="hg-input" placeholder={t('orders.streetPlaceholder')} value={newOrder.street}
                onChange={e => setNewOrder(d => ({ ...d, street: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
            </div>

            {/* Items */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <p style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:13, color:'#454652', margin:0 }}>{t('orders.itemsHeader')}</p>
              <button onClick={addItem} style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(0,6,102,0.07)', color:'#000666', border:'none', cursor:'pointer' }}>
                <Plus size={12}/>{t('orders.addProduct')}
              </button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
              {newOrder.items.map((item, i) => (
                <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <div style={{ flex:1 }}>
                    <input className="hg-input" placeholder="Product ID (uuid)" dir="ltr" value={item.productId}
                      onChange={e => setItem(i, 'productId', e.target.value)}
                      style={{ fontFamily:'monospace', fontSize:12 }} />
                  </div>
                  <div style={{ width:80 }}>
                    <input type="number" min={1} className="hg-input" placeholder={t('orders.quantityPlaceholder')} value={item.quantity}
                      onChange={e => setItem(i, 'quantity', parseInt(e.target.value) || 1)}
                      style={{ fontFamily:'Cairo, sans-serif', textAlign:'center' }} />
                  </div>
                  {newOrder.items.length > 1 && (
                    <button onClick={() => removeItem(i)} style={{ width:32, height:32, borderRadius:8, background:'rgba(186,26,26,0.08)', color:'#BA1A1A', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Trash2 size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Note */}
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:11, color:'#767683', marginBottom:5 }}>{t('orders.noteLabel')}</label>
              <textarea rows={2} className="hg-input" style={{ resize:'none', fontFamily:'Tajawal, sans-serif' }} placeholder={t('orders.notePlaceholder')}
                value={newOrder.note} onChange={e => setNewOrder(d => ({ ...d, note: e.target.value }))} />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={handleSubmitOrder}
                disabled={!newOrder.userId.trim() || !newOrder.street.trim() || !newOrder.city.trim() || !newOrder.recipientName.trim()}
                style={{ flex:1, padding:'12px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#000666', border:'none', cursor:'pointer', boxShadow:'0 8px 24px rgba(26,35,126,0.2)',
                  opacity:(!newOrder.userId.trim() || !newOrder.street.trim() || !newOrder.city.trim()) ? 0.5 : 1 }}>
                {t('orders.createOrder')}
              </button>
              <button onClick={() => setAddOrderOpen(false)}
                style={{ flex:1, padding:'12px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
